import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {AUTOMATE_WITHDRAWAL} from 'graphql/mutations/wallet';
import {toast} from 'react-toastify';
import {useState} from 'react';
import styles from './walletTransaction.module.scss';
import {currencySymbols} from 'utils/mock';
import {providerStatusLabel} from './ProviderStatusTag';

const formatAmount = value => {
  const amount = Number(value?.$numberDecimal ?? value);
  return isNaN(amount) ? '-' : amount.toLocaleString();
};

/**
 * The transfer a status check returned, or null. It arrives as
 * `{transaction}` on some provider paths and as the bare record on others,
 * and `data` is free-form JSON, so it may also come as a JSON string.
 */
export const readTransferStatus = data => {
  let value = data;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  const transfer =
    value && typeof value === 'object' ? value.transaction ?? value : null;
  return transfer && typeof transfer === 'object' && !Array.isArray(transfer)
    ? transfer
    : null;
};

const rawResponse = data => {
  if (data === null || data === undefined) return 'Nothing (empty response)';
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch (error) {
      return data;
    }
  }
  return JSON.stringify(data, null, 2);
};

export const AutomateTransferModal = ({refetch, onClose, content, type}) => {
  // After a status check: {transfer, raw, message}. `transfer` is null when
  // the server sent nothing readable; the modal still opens to say so.
  const [statusResult, setStatusResult] = useState(null);
  const automationStatus = statusResult?.transfer;
  const [automateWithdrawal, {loading}] = useMutation(AUTOMATE_WITHDRAWAL, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const handleConfirm = () => {
    automateWithdrawal({
      variables: {
        input: {
          id: content.id,
          currency: content.currency,
          ...(type !== 'TRANSFER' && {
            outcome: true,
          }),
        },
      },
    })
      .then(({data: {automateWithdrawal}}) => {
        if (
          automateWithdrawal.statusCode === 400 ||
          automateWithdrawal.__typename === 'Error'
        ) {
          return toast.error(automateWithdrawal.message);
        } else {
          toast.success(automateWithdrawal.message);

          // A status check always shows its result — including an empty one —
          // so a missing reply from the backend is visible rather than looking
          // like a modal that never opened.
          if (type !== 'TRANSFER') {
            setStatusResult({
              transfer: readTransferStatus(automateWithdrawal.data),
              raw: automateWithdrawal.data,
              message: automateWithdrawal.message,
            });
          } else {
            refetch();
            onClose();
          }
        }
      })
      .catch(error => toast.error(error.message));
  };

  const symbol =
    currencySymbols[automationStatus?.currency ?? content?.currency]?.symbol;

  const handleClose = () => {
    // The check may have moved the row on, so the table should catch up.
    if (statusResult) refetch();
    setStatusResult(null);
    onClose();
  };

  return (
    <div className="confirm-modal">
      {type === 'TRANSFER' ? (
        <>
          <div className={styles.modalHeader}>
            <h1>Automate Withdrawal</h1>
            <p>Are you sure you want to automate this withdrawal?</p>
          </div>
          <div className={styles.confirmationDetails}>
            <div className={styles.detailItem}>
              <span className={styles.label}>User:</span>
              <span className={styles.value}>
                {content?.user?.firstName} {content?.user?.lastName}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Type:</span>
              <span className={styles.value}>{content?.type}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Currency:</span>
              <span className={styles.value}>{content?.currency}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Status:</span>
              <span className={styles.value}>{content?.status}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Provider Status:</span>
              <span className={styles.value}>
                {providerStatusLabel(content?.providerStatus) ?? '—'}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>User Receives:</span>
              <span className={styles.value}>
                {content?.transactionAmountNetOfFees?.toLocaleString()}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.label}>Transaction Fee:</span>
              <span className={styles.value}>
                {content?.transactionFee?.toLocaleString()}
              </span>
            </div>
          </div>
          <div className={styles.modalButtons}>
            <Button type="secondary" label="Cancel" onClick={onClose} />
            <Button
              label="Confirm"
              onClick={handleConfirm}
              disabled={loading}
            />
          </div>
        </>
      ) : (
        <div>
          <div className={styles.modalHeader}>
            <h1>Check Automation Status</h1>
            {!statusResult ? (
              <p>Are you sure you want to check this withdrawal?</p>
            ) : automationStatus ? (
              <p>Withdrawal Status Retrieved Successfully!</p>
            ) : (
              <p>The server sent no transfer details for this withdrawal.</p>
            )}
          </div>

          {statusResult ? (
            <>
              {automationStatus && (
                <div className={styles.confirmationDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Transaction ID:</span>
                    <span className={styles.value}>{automationStatus?.id}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Reference ID:</span>
                    <span className={styles.value}>
                      {automationStatus?.referenceId}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Type:</span>
                    <span className={styles.value}>
                      {automationStatus?.type}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Total Amount:</span>
                    <span className={styles.value}>
                      {symbol}{' '}
                      {formatAmount(
                        automationStatus?.totalTransactionAmountIncludingFees ??
                          automationStatus?.amount,
                      )}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>User Receives:</span>
                    <span className={styles.value}>
                      {symbol}{' '}
                      {formatAmount(
                        automationStatus?.transactionAmountNetOfFees,
                      )}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Transaction Fee:</span>
                    <span className={styles.value}>
                      {symbol} {formatAmount(automationStatus?.transactionFee)}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Status:</span>
                    <span className={styles.value}>
                      {automationStatus?.status}
                    </span>
                  </div>
                  {automationStatus?.providerStatus && (
                    <div className={styles.detailItem}>
                      <span className={styles.label}>Provider Status:</span>
                      <span className={styles.value}>
                        {providerStatusLabel(automationStatus.providerStatus)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className={styles.rawResponse}>
                <span className={styles.label}>What the server sent</span>
                {statusResult.message && (
                  <span className={styles.value}>{statusResult.message}</span>
                )}
                <pre>{rawResponse(statusResult.raw)}</pre>
              </div>
              <div className={styles.modalButtons}>
                <Button type="secondary" label="Close" onClick={handleClose} />
              </div>
            </>
          ) : (
            <div className={styles.modalButtons}>
              <Button type="secondary" label="Cancel" onClick={onClose} />
              <Button
                label="Confirm"
                onClick={handleConfirm}
                disabled={loading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
